/**
 * Formats a timestamp to milliseconds by padding to 13 digits
 * Handles timestamps in seconds (10 digits), milliseconds (13 digits), or other formats
 * 
 * @param timestamp - The timestamp to format (can be in seconds or milliseconds)
 * @returns Timestamp in milliseconds (13 digits)
 */
export const normalizeTimestamp = (timestamp: number): number => {
  if (!timestamp || timestamp === 0) return 0;
  
  const timestampStr = timestamp.toString();
  const digitsNeeded = 13 - timestampStr.length;
  
  // If timestamp needs padding, multiply by 10^digitsNeeded
  // If timestamp is already 13+ digits, use as is
  return digitsNeeded > 0 
    ? timestamp * Math.pow(10, digitsNeeded) 
    : timestamp;
};

/**
 * Formats a timestamp to a localized date string
 * 
 * @param timestamp - The timestamp to format (can be in seconds or milliseconds)
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string
 */
export const formatTimestamp = (
  timestamp: number, 
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!timestamp || timestamp === 0) return 'N/A';
  
  const milliseconds = normalizeTimestamp(timestamp);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  
  return new Date(milliseconds).toLocaleDateString("en-US", options || defaultOptions);
};

/**
 * Formats a timestamp to a localized date and time string
 * 
 * @param timestamp - The timestamp to format (can be in seconds or milliseconds)
 * @returns Formatted date and time string
 */
export const formatTimestampFull = (timestamp: number): string => {
  if (!timestamp || timestamp === 0) return 'N/A';
  
  const milliseconds = normalizeTimestamp(timestamp);
  return new Date(milliseconds).toLocaleString("en-US");
};

/**
 * Formats a timestamp to a simple date string (without time)
 * 
 * @param timestamp - The timestamp to format (can be in seconds or milliseconds)
 * @returns Formatted date string
 */
export const formatTimestampDate = (timestamp: number): string => {
  if (!timestamp || timestamp === 0) return 'N/A';
  
  const milliseconds = normalizeTimestamp(timestamp);
  return new Date(milliseconds).toLocaleDateString("en-US");
};

